// @flow

import React, { useState, useContext } from 'react';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { Box } from '@latitude/box';
import FeaturesSlider from '@/components/FeaturesSlider/FeaturesSlideV2';
import { StickyCta } from '@latitude/sticky-cta';
import { Heading4 } from '@latitude/heading';
import StickyNavigation from '@/components/StickyNavigation/StickyNavigationV2';
import { COLOR, MARGIN, POSITION } from '@latitude/core/utils/constants';
import Layout from '../../components/layout';
import PageData from '../../data/pages/personal-loan.json';
import footerData from '../../data/json/footer.json';
import MobileAppInstallPrompts from '@latitude/mobile-app-install-prompt/MobileAppInstallPrompt';
import { featuresSliderIE11Fix } from '../../utils/stylesUtils';
import { BREAKPOINT } from '../../utils/constants';
import useApplyUrl from '../../hooks/useApplyUrl';
import SoftQuoteContext from '../../context/SoftQuoteContext';
import SvgInline from '@latitude/svg-inline';
import { PageContext } from '../../context/PageContext';
import HeroBanner from '@/components/lab-components/HeroBanner';
import PageHeaderSection from '@/components/PageLayout/PageHeaderSection';
import { SOFT_QUOTE_LOANS_URL } from '../../utils/softQuoteUtil';
import Section from '@latitude/section-rebrand';
import ProductComparisonSection from '@/components/ProductComparisonSection/ProductComparisonSectionV2';
import CardCollection from '@/components/lab-components/CardCollection/CardCollectionV2';
import ProofPointCollection from '@/components/lab-components/ProofPointCollection/ProofPointCollectionV2';
import PromoBanner from '@/components/lab-components/PromoBanner/PromoBannerV2';
import TwoByTwoGridCollection from '@/components/lab-components/TwoByTwoGridCollection';
import Faq from '@/components/Faq/FaqV2/Faq';
import PersonalLoanCalculator from '@/components/PersonalLoanCalculatorRebrand/PersonalLoanCalculator';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import Button from '@latitude/button-rebrand';
import Typography from '@latitude/typography';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';

/*
 * Personal Loan Page
 */
const COLOUR_PALE_BLUE = '#E5F6FF';
const PL_APPLY_CTA_TRACK_ID = 'calc-apply-now';
const PersonalLoanPage = () => {
  const [isNavSticky, setIsNavSticky] = useState(false);
  const { applyUrl, setApplyUrl } = useApplyUrl();

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  const featureSliderData = state?.featureSliderData || [
    {
      heading: 'Why choose a Gem Loan?',
      featureCards: PageData.content.whyChooseLatitude
    }
  ];
  /** - END - */
  const getEstimateUrl = 'https://loans.portal.gemfinance.co.nz/s/quote';

  return (
    <Layout noMetaKeywords customFooter={footerData}>
      <MobileAppInstallPrompts />
      <main
        className="navigation-spacer personal-loan-page"
        css={`
          #financial-reset {
            scroll-margin-top: 136px;
          }
        `}
      >
        <Box backgroundColor={COLOR.GREY6}>
          <Helmet>
            <link
              rel="canonical"
              href="https://www.gemfinance.co.nz/loans/personal-loans/"
            />
            <title>
              Personal Loans NZ | Fixed Interest Rates | Gem by Latitude
            </title>
            <meta
              name="description"
              content="Need a Personal Loan in New Zealand for a new car, home renovation, travel or consolidate debt? With Gem Finance personal loan get a quote in under 2 mins."
            />
          </Helmet>
          <HeroV2
            {...(state?.heroBannerData?.[0] ||
              PageData?.contentful?.heroBannerData?.[0])}
          />

          <SoftQuoteContext.Provider value={{ applyUrl, setApplyUrl }}>
            <StickyNavigation
              items={PageData?.contentful?.inPageNavData?.items}
              enableAnchorUpdate={true}
              isSticky={isNavSticky}
              onStickyNavStateChange={setIsNavSticky}
              ctaTarget="_self"
              offsetElem="[data-sticky-navigation-offset]"
              trackId="sticky-nav-get-started"
              trackEventData={{
                category: 'cta',
                action: 'quote-link',
                label: 'Get rate estimate',
                location: 'Sticky Navigation'
              }}
              trackProductId={['PLNZLF-WEB']}
              {...state?.inPageNavData?.[0]}
            />
          </SoftQuoteContext.Provider>

          <FeaturesSlider
            key={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).heading
            }
            heading={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).heading
            }
            id="why-us"
            className="why-choose bg-f8f8f8"
            subheading={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).description
            }
            backgroundColor={
              state?.featureSliderData?.[0]?.backgroundColor?.value ||
              PageData?.contentful?.featureSliderData[0]?.backgroundColor?.value
            }
            data={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).featureCards
            }
          />
          <div
            css={`
              background-color: ${REBRAND_COLOR.BLACK_20};
              padding-top: 32px;
              padding-bottom: 32px;
              && .typography-base__sh1 {
                margin-bottom: 30px !important;
              }
            `}
          >
            <Section id="check-your-rate">
              <Typography.SH1 color={REBRAND_COLOR.BLACK} semibold>
                Get an estimate of your repayments
              </Typography.SH1>
              <PersonalLoanCalculator
                applyUrl={getEstimateUrl}
                applyCTATrackProductId={['PLNZLF-WEB']}
              />
            </Section>
          </div>
          <TwoByTwoGridCollection
            id="financial-reset"
            data={
              state?.cardCollectionData?.[1] ||
              PageData?.contentful?.cardCollectionData?.[1]
            }
          />
          <Box id="top-up">
            {state?.promoBannerData?.[2] && (
              <PromoBanner {...state?.promoBannerData?.[2]} />
            )}
          </Box>

          <ProductComparisonSection
            id="compare-products"
            {...state?.productComparisonData?.[0]}
          />

          <ProofPointCollection
            id="calculators-and-tools"
            data={
              state?.proofPointCollectionData?.[0] ||
              PageData?.contentful?.proofPointCollectionData?.[0]
            }
          />

          <CardCollection
            id="applying-is-simple"
            data={
              state?.cardCollectionData?.[0] ||
              PageData?.contentful?.cardCollectionData?.[0]
            }
          />

          <PromoBanner
            {...(state?.promoBannerData?.[0] ||
              PageData?.contentful?.promoBannerData?.[0])}
          />

          <PromoBanner
            {...(state?.promoBannerData?.[1] ||
              PageData?.contentful?.promoBannerData?.[1])}
          />

          <Faq data={PageData.content.faq} {...state?.faqData?.[0]} />

          <ImportantInformation
            data={require('../../data/pages/personal-loan.json')}
            css={`
              background-color: ${REBRAND_COLOR.STONE};
              overflow-x: hidden;
              .row {
                @media (min-width: 1600px) {
                  padding-left: ${PADDING.P32};
                  padding-right: ${PADDING.P32};
                }
              }
            `}
            {...{
              ...state?.importantInfoData?.[0],
              sectionOneColummClasses: 'col-12 col-lg-6',
              sectionTwoColummClasses: 'col-12 col-lg-6'
            }}
          />
        </Box>
      </main>
    </Layout>
  );
};

export default PersonalLoanPage;
